import * as React from 'react'
import { Menu } from 'lucide-react'

import { ModuleNavigationSelectProvider } from './module-navigation'
import { Button } from './ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet'
import { cn } from '@ui/lib/utils'

export interface ModuleSidebarLayoutProps {
  title: string
  description: string
  navigation: React.ReactNode
  footer?: React.ReactNode
  children: React.ReactNode
  navigationLabel?: string
  className?: string
}

export function ModuleSidebarLayout({
  title,
  description,
  navigation,
  footer,
  children,
  navigationLabel = 'Navigation',
  className,
}: ModuleSidebarLayoutProps) {
  const [open, setOpen] = React.useState(false)
  const titleId = React.useId()
  const descriptionId = React.useId()
  const hasNavigation = React.Children.toArray(navigation).length > 0
  const closeNavigation = React.useCallback(() => setOpen(false), [])

  return (
    <div
      data-slot="module-sidebar-layout"
      className={cn(
        'relative -ml-[var(--berky-shell-content-padding)] flex w-[calc(100%+var(--berky-shell-content-padding)+var(--berky-shell-content-padding))] max-w-none lg:left-1/2 lg:ml-0 lg:-mt-[var(--berky-shell-content-padding)] lg:-mb-[var(--berky-shell-content-bottom-space)] lg:h-[calc(100dvh-var(--berky-shell-header-height))] lg:min-h-0 lg:w-dvw lg:-translate-x-1/2 lg:overflow-clip',
        className,
      )}
    >
      {hasNavigation ? (
        <aside className="hidden w-72 shrink-0 overflow-y-auto border-r bg-sidebar px-6 py-6 text-sidebar-foreground lg:flex lg:flex-col">
          {navigation}
          {footer ? <div className="mt-auto pt-8">{footer}</div> : null}
        </aside>
      ) : null}

      <div
        data-testid="module-sidebar-content"
        className="min-w-0 flex-1 overflow-x-clip p-4 md:p-8 lg:overflow-y-auto lg:pb-[calc(var(--berky-shell-content-bottom-space)+3rem)] xl:px-10"
      >
        {hasNavigation ? (
          <div className="mb-4 lg:hidden">
            <Sheet modal open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button type="button" variant="outline">
                  <Menu aria-hidden="true" />
                  {navigationLabel}
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
                className="w-[min(22rem,calc(100dvw-2rem))]"
              >
                <SheetHeader>
                  <SheetTitle id={titleId}>{title}</SheetTitle>
                  <SheetDescription id={descriptionId}>{description}</SheetDescription>
                </SheetHeader>
                <ModuleNavigationSelectProvider value={closeNavigation}>
                  <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
                    {navigation}
                  </div>
                </ModuleNavigationSelectProvider>
                {footer ? <SheetFooter>{footer}</SheetFooter> : null}
              </SheetContent>
            </Sheet>
          </div>
        ) : null}
        {children}
      </div>
    </div>
  )
}
